import { useAppDispatch, useAppSelector } from 'src/hooks';
import { useLayoutEffect } from 'react';
import {
  accountSelector,
  errorAccountLoadingSelector,
  lastAccessedSiteIdSelector,
  lastAccessedWidgetSelector,
  loadingAccountSelector,
} from 'src/redesign/features/Account/store/selectors';
import { fetchSettingsApi, getFonts } from 'src/features/settings-slice';
import { getAccountInfo } from 'src/redesign/features/Account/store/actions';
import { useHistory } from 'react-router-dom';
import { fetchAllUserSites } from 'src/redesign/features/Sites/actions';
import { fetchSiteDataById } from 'src/redesign/features/SelectedSite/store/actions';
import { fetchWidgetData } from 'src/redesign/features/Widgets/actions';
import { isPublicRoute } from 'src/app';

// Module-level cache that persists across component mounts/unmounts
// This prevents duplicate fetches when components remount
const globalFetchCache = {
  settings: false,
  sites: false,
  fonts: false,
  siteId: null as string | null,
  widgetId: null as string | null,
  lastAccountId: null as string | null,
};

export function useFetchAllData() {
  const dispatch = useAppDispatch();
  const history = useHistory();

  const account = useAppSelector(accountSelector);
  const loading = useAppSelector(loadingAccountSelector);
  const error = useAppSelector(errorAccountLoadingSelector);
  const lastSiteAccessedId = useAppSelector(lastAccessedSiteIdSelector);
  const lastWidgetAccessedId = useAppSelector(lastAccessedWidgetSelector);

  useLayoutEffect(() => {
    if (isPublicRoute()) {
      return;
    }

    if (error) {
      history.push('/auth/login');
      return;
    }

    // No account and not loading -> fetch account info
    if (!account) {
      dispatch(getAccountInfo());
      return;
    }

    // If account changes (different user), reset the cache
    if (account?.id && account.id !== globalFetchCache.lastAccountId) {
      globalFetchCache.settings = false;
      globalFetchCache.sites = false;
      globalFetchCache.fonts = false;
      globalFetchCache.siteId = null;
      globalFetchCache.widgetId = null;
      globalFetchCache.lastAccountId = account.id;
    }

    // If account exists and loading finished
    if (account && !loading) {
      // Only fetch settings once per session
      if (!globalFetchCache.settings) {
        dispatch(fetchSettingsApi());
        globalFetchCache.settings = true;
      }

      // Only fetch sites and fonts once per session
      if (!globalFetchCache.sites) {
        dispatch(fetchAllUserSites());
        globalFetchCache.sites = true;
      }

      if (!globalFetchCache.fonts) {
        dispatch(getFonts());
        globalFetchCache.fonts = true;
      }

      // Only fetch site/widget data when they change
      const siteChanged = lastSiteAccessedId !== globalFetchCache.siteId;
      const widgetChanged = lastWidgetAccessedId !== globalFetchCache.widgetId;

      if (siteChanged) {
        dispatch(fetchSiteDataById(lastSiteAccessedId));
        globalFetchCache.siteId = lastSiteAccessedId;
      }

      if (siteChanged || widgetChanged) {
        dispatch(fetchWidgetData({ siteId: lastSiteAccessedId, widgetId: lastWidgetAccessedId }));
        globalFetchCache.widgetId = lastWidgetAccessedId;
      }
    }
  }, [account, loading, error, dispatch, history, lastSiteAccessedId, lastWidgetAccessedId]);

  return null;
}
